import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import Select from "react-select";
import CardLayout from "../../../../common/cardLayout/CardLayout";
import "./PinnedContentTableStyles.css";
import PatientLoader from "../../../../common/PatientLoader";
import { notify } from "../../../../../util/helpers";
import axiosInstance, { CMS_BASE_URL } from "../../../../../axios";
import {
  fetchPatientPCLogic,
  pinContentForPatientLogic,
} from "../../../../../store/features/patient/pinnedContentSlice";
import { useDispatch } from "react-redux";

const customStyles = {
  overlay: {
    backgroundColor: "rgba(11, 17, 71, 0.5)",
  },
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    borderRadius: "16px",
    transform: "translate(-50%, -50%)",
    width: "60%",
  },
};

const PinNewContent = ({ modalIsOpen, setModalOpen, patientID }) => {
  Modal.setAppElement("#root");
  //TODO: Memoize this and PinnedContent Pages

  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [selectedContent, setSelectedContent] = useState(null);
  const [categories, setCategories] = useState([]);
  const [media, setMedia] = useState([
    {
      label: "Videos",
      value: "Videos",
    },
    {
      label: "Audio",
      value: "Audio",
    },
    {
      label: "Article",
      value: "Article",
    },
    // {
    //   label: "TikToks",
    //   value: "TikToks",
    // },
  ]);
  const [content, setContent] = useState([]);

  const [formattedData, setFormattedData] = useState([]);
  const [data, setData] = useState([]);

  const closeModal = () => {
    setSelectedCategory(null);
    setSelectedMedia(null);
    setSelectedContent(null);
    setModalOpen(false);
  };

  const isMediaMatch = (mediaType, selectedLabel) => {
    if (!mediaType || !selectedLabel) return false;
    const type = String(mediaType).toLowerCase().trim();
    const target = String(selectedLabel).toLowerCase().trim();
    if (target === "videos") {
      return ["videos", "video", "viewmedica", "view-medica", "view-medicas"].includes(type);
    }
    if (target === "audio") {
      return ["audio", "audios"].includes(type);
    }
    if (target === "article") {
      return ["article", "articles", "post", "posts"].includes(type);
    }
    return type === target;
  };

  const normalizeAppType = (val) => {
    if (!val) return null;
    if (typeof val === "object") {
      return val.App || val.app || null;
    }
    if (val === 1 || val === "1") return "Youth";
    if (val === 2 || val === "2") return "Adult";
    if (val === 3 || val === "3") return "YouthAndAdult";
    if (typeof val === "string") return val.trim();
    return null;
  };

  const isAppTypeAllowed = (rawAppType, dashboardType) => {
    const norm = normalizeAppType(rawAppType);
    if (!norm) return true;
    if (dashboardType === "youth") {
      return norm !== "Adult";
    } else {
      return norm !== "Youth";
    }
  };

  const handlePinnedContentFormatting = async () => {
    setLoading(true);
    const dashboardType = process.env.REACT_APP_DASHBOARD_TYPE || "youth";

    try {
      const res = await axiosInstance.get(`${CMS_BASE_URL}/classifications/`, {
        params: { isPinable: "true" },
      });
      let rawClassifications = Array.isArray(res?.data) ? [...res.data] : [];

      const hasMeditationsWithAudios = rawClassifications.some(
        (c) =>
          (c?.Name || c?.name || "").toLowerCase().includes("meditation") &&
          Array.isArray(c?.audios) &&
          c.audios.length > 0
      );

      if (!hasMeditationsWithAudios) {
        try {
          const audioRes = await axiosInstance.get(`${CMS_BASE_URL}/content-type/audios/`);
          const audioList = Array.isArray(audioRes?.data) ? audioRes.data : [];
          if (audioList.length > 0) {
            const existingMedIndex = rawClassifications.findIndex((c) =>
              (c?.Name || c?.name || "").toLowerCase().includes("meditation")
            );
            if (existingMedIndex !== -1) {
              rawClassifications[existingMedIndex] = {
                ...rawClassifications[existingMedIndex],
                isPinable: true,
                audios: audioList,
              };
            } else {
              rawClassifications.push({
                id: 27,
                Name: "Meditations",
                name: "Meditations",
                isVisible: true,
                isVisibleYouth: true,
                isPinable: true,
                audios: audioList,
                posts: [],
                view_medicas: [],
                videos: [],
              });
            }
          }
        } catch (audioErr) {
          console.warn("Could not fetch standalone audios fallback:", audioErr);
        }
      }

      const tempCategories = [];
      const allNormalizedContent = [];

      rawClassifications.forEach((cat) => {
        const cname = cat?.Name || cat?.name || "";
        const isPinable = cat?.isPinable;
        const isVisibleYouth = cat?.isVisibleYouth;

        if (isPinable === false || isPinable === "false") {
          return;
        }

        if (dashboardType === "youth") {
          const isMeditation = cname.toLowerCase().includes("meditation");
          if (!isMeditation && (isVisibleYouth === false || isVisibleYouth === "false")) {
            return;
          }
        }

        const catItems = [];

        (cat?.posts || []).forEach((item) => {
          if (isAppTypeAllowed(item?.app_type, dashboardType)) {
            catItems.push({
              ...item,
              media_type: "Article",
              classification: { id: cat.id, Name: cname, name: cname },
            });
          }
        });

        [...(cat?.view_medicas || []), ...(cat?.videos || [])].forEach((item) => {
          if (isAppTypeAllowed(item?.app_type, dashboardType)) {
            catItems.push({
              ...item,
              media_type: "Videos",
              classification: { id: cat.id, Name: cname, name: cname },
            });
          }
        });

        (cat?.audios || []).forEach((item) => {
          if (isAppTypeAllowed(item?.app_type, dashboardType)) {
            catItems.push({
              ...item,
              media_type: "Audio",
              classification: { id: cat.id, Name: cname, name: cname },
            });
          }
        });

        if (catItems.length > 0) {
          tempCategories.push({
            label: cname,
            value: cname,
            id: cat?.id,
          });
          allNormalizedContent.push(...catItems);
        }
      });

      setCategories(tempCategories);
      setData(allNormalizedContent);
    } catch (error) {
      console.error("Error fetching classifications:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handlePinnedContentFormatting();
  }, []);

  const handleNewPinContent = () => {
    if (!selectedCategory || !selectedMedia || !selectedContent) {
      notify("You need to select a category, media type and item!", "error");
      return;
    }

    pinContentForPatientLogic({
      identifier: selectedContent?.value?.id,
      patient: patientID.toString(),
      type: selectedMedia?.value,
    })
      .then((res) => {
        if (res.status === 200 || res.status === 201) {
          notify("Content pinned successfully!");
          dispatch(fetchPatientPCLogic(patientID));
          closeModal();
        }
      })
      .catch((err) => {
        console.log("Error pinning content", err);
        notify("Something went wrong!", "error");
      });
  };

  useEffect(() => {
    if (categories.length > 0 && !selectedCategory) {
      setSelectedCategory(categories[0]);
      setSelectedMedia(media[0]);
    }
  }, [categories]);

  useEffect(() => {
    if (selectedCategory && selectedMedia) {
      const temp = [];
      data.forEach((contentItem) => {
        const itemTitle = contentItem?.Title || contentItem?.title;
        const itemMediaType = contentItem?.media_type || contentItem?.mediaType;
        const itemClassName = contentItem?.classification?.Name || contentItem?.classification?.name;
        if (
          isMediaMatch(itemMediaType, selectedMedia?.label) &&
          itemClassName === selectedCategory?.label
        ) {
          temp.push({
            label: itemTitle,
            value: contentItem,
          });
        }
      });
      setContent(temp);
      if (temp.length > 0) {
        setSelectedContent(temp[0]);
      } else {
        setSelectedContent(null);
      }
    }
  }, [selectedCategory, selectedMedia, data]);

  return (
    <div>
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        style={customStyles}
        contentLabel=""
        ariaHideApp={false}
      >
        <CardLayout>
          <div className="container-fluid d-flex mt-4">
            <h3 className="primary-text-blue">Pin New Content</h3>
          </div>
          <div className="container-fluid px-5 py-2">
            {loading ? (
              <PatientLoader />
            ) : (
              <>
                <div className="container-fluid row py-4 px-0">
                  <div className="col-sm-12 col-md-6 col-lg-6 container-fluid">
                    <div className="form-group mb-4">
                      <label>Select Category</label>

                      <Select
                        styles={{
                          control: (baseStyles) => ({
                            ...baseStyles,
                            border: "0px",
                            borderBottom: "1px solid #94A3B8",
                            borderRadius: "0px",
                            padding: "5px",
                          }),
                        }}
                        components={{
                          IndicatorSeparator: () => null,
                        }}
                        placeholder="Select"
                        classNamePrefix="select"
                        name="category"
                        value={selectedCategory}
                        onChange={(e) => {
                          setSelectedCategory(e);
                        }}
                        options={categories}
                      />
                    </div>
                    <div className="form-group mb-4">
                      <label>Select Media</label>

                      <Select
                        styles={{
                          control: (baseStyles) => ({
                            ...baseStyles,
                            border: "0px",
                            borderBottom: "1px solid #94A3B8",
                            borderRadius: "0px",
                            padding: "5px",
                          }),
                        }}
                        components={{
                          IndicatorSeparator: () => null,
                        }}
                        placeholder="Select"
                        classNamePrefix="select"
                        name="media"
                        value={selectedMedia}
                        onChange={(e) => {
                          setSelectedMedia(e);
                        }}
                        options={media}
                      />
                    </div>
                    <div className="form-group mb-4">
                      <label>Select title to pin</label>

                      <Select
                        styles={{
                          control: (baseStyles) => ({
                            ...baseStyles,
                            border: "0px",
                            borderBottom: "1px solid #94A3B8",
                            borderRadius: "0px",
                            padding: "5px",
                          }),
                        }}
                        components={{
                          IndicatorSeparator: () => null,
                        }}
                        placeholder="Select"
                        classNamePrefix="select"
                        name="content"
                        value={selectedContent}
                        onChange={(e) => {
                          setSelectedContent(e);
                        }}
                        options={content}
                      />
                    </div>
                  </div>
                  <div className="col-sm-12 col-md-6 col-lg-6 container-fluid">
                    {selectedContent ? (
                      <>
                        {selectedMedia?.label === "Videos" && (
                          <div
                            className="preview-section"
                            dangerouslySetInnerHTML={{
                              __html: selectedContent?.value?.Embed,
                            }}
                          />
                        )}

                        {/* {selectedMedia?.label === "TikToks" && (
                          <div
                            className="preview-section"
                            dangerouslySetInnerHTML={{
                              __html: selectedContent?.value?.TikTokEmbed,
                            }}
                          />
                        )} */}

                        {selectedMedia?.label === "Audio" && (
                          <div className="text-center container-fluid">
                            <video
                              width="100%"
                              height="100%"
                              controls
                              name="media"
                            >
                              <source
                                src={selectedContent?.value?.Audio?.url}
                                type="audio/mpeg"
                              />
                            </video>
                          </div>
                        )}

                        {selectedMedia?.label === "Article" && (
                          <textarea
                            style={{
                              height: "100%",
                              width: "100%",
                              userSelect: "none",
                              resize: "none",
                              border: "none",
                              outline: "none",
                            }}
                            readOnly
                            value={selectedContent?.value?.Body}
                          />
                        )}
                      </>
                    ) : (
                      <div className="preview-section-blank">
                        <span>Select an item to preview</span>
                      </div>
                    )}
                  </div>
                </div>
                <div
                  style={{ gap: "10px" }}
                  className="container-fluid d-flex justify-content-end mt-4 px-2"
                >
                  <button
                    className="btn alternative-btn"
                    style={{ width: "25%" }}
                    onClick={closeModal}
                  >
                    Cancel
                  </button>
                  <button
                    disabled={
                      !(selectedCategory && selectedContent && selectedMedia)
                    }
                    style={{ width: "25%" }}
                    className="primary-btn"
                    onClick={handleNewPinContent}
                  >
                    Pin Content
                  </button>
                </div>
              </>
            )}
          </div>
        </CardLayout>
      </Modal>
    </div>
  );
};

export default PinNewContent;
